import { useState, type FormEvent } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { customerApiClient } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { useTurnstileGate } from "@/hooks/use-turnstile-gate";
import { TurnstileWidget } from "@/components/turnstile-widget";
import { Button, buttonClasses, FormField, inputClass } from "@/components/ui";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ForgotPasswordBody {
  email: string;
  slug: string;
  turnstile_token?: string;
}

export const Route = createFileRoute("/portal/$slug/forgot-password")({
  component: ForgotPasswordPage,
});

/**
 * Requests a reset link for the utility whose page this is: the request
 * carries the page's slug, and the emailed link opens that utility's reset
 * page. The server answers 204 whether or not an account uses the address, and
 * so does the page: it never says which addresses hold accounts.
 */
function ForgotPasswordPage() {
  const { slug } = Route.useParams();
  const { t } = useTranslation(["customer", "common"]);
  const { apiError: showApiError } = useToast();
  const gate = useTurnstileGate();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Covers the window isPending does NOT: gate.freshToken() resets the widget
  // and waits on the next solve, and for that whole span no mutation has
  // started, so a second click would burn a second challenge.
  const [submitting, setSubmitting] = useState(false);

  const forgotMutation = useMutation({
    mutationFn: async (body: ForgotPasswordBody) => {
      const { error } = await customerApiClient.POST("/auth/forgot-password", { body });
      if (error) throw error;
    },
    // The client throws the API's error envelope; only its code picks the toast.
    onError: (err: unknown) => showApiError((err as { code?: string })?.code),
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const email = String(new FormData(e.currentTarget).get("email") ?? "").trim();

    if (!email) {
      setFieldErrors({ email: t("common:validation.required") });
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setFieldErrors({ email: t("common:validation.invalidEmail") });
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    // Every response is single-use. Reading the mount solve off the form
    // replays a spent token on the second attempt, which siteverify answers
    // `timeout-or-duplicate`.
    void gate
      .freshToken()
      .then((turnstileToken) => {
        // A challenge we do not have is a field we do not send: with no site
        // key the widget never mounts, and "" would claim an empty response
        // instead of no response.
        forgotMutation.mutate({
          email,
          slug,
          ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
        });
      })
      .finally(() => setSubmitting(false));
  };

  const sentTo = forgotMutation.isSuccess ? forgotMutation.variables.email : null;
  const busy = submitting || forgotMutation.isPending;

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <h2 className="text-xl font-semibold tracking-[-0.01em] text-ink">
          {t("portal.forgot.title")}
        </h2>
        {sentTo === null && <p className="text-sm text-body">{t("portal.forgot.body")}</p>}
      </div>
      {sentTo === null ? (
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <FormField label={t("portal.signIn.email")} error={fieldErrors.email}>
            <input name="email" type="email" autoComplete="email" className={inputClass} />
          </FormField>
          {/* The server skips verification when its secret is empty (dev), so
              an unset site key renders nothing at all. */}
          {gate.enabled && (
            <TurnstileWidget siteKey={gate.siteKey} onToken={gate.onToken} ref={gate.widgetRef} />
          )}
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? t("portal.forgot.submitting") : t("portal.forgot.submit")}
          </Button>
        </form>
      ) : (
        <p role="status" className="text-sm text-body">
          {t("portal.forgot.sent", { email: sentTo })}
        </p>
      )}
      <Link to="/portal/$slug" params={{ slug }} className={buttonClasses("link", "sm")}>
        {t("portal.forgot.back")}
      </Link>
    </div>
  );
}
